import {
  Firestore,
  UnregisteredNotificationsCollection,
  UnregisteredNotificationsDoc,
  NotificationsUnregisteredRetrieve,
} from '@/firebase-exports'

/**
 * Recupera las unregisteredNotifications del Dms.
 *
 * @returns {Promise<Array>} Una promesa que se resuelve con un array de notificaciones.
 */
export async function notificationsUnregisteredRetrieve(userId) {
  return await NotificationsUnregisteredRetrieve({ userId })
}

/**
 * Lee un documento unregisteredNotification específico.
 *
 * @param {string} userId - ID del usuario.
 * @param {string} notificationId - ID de la notificación a recuperar.
 * @returns {Promise<Object>} Una promesa que se resuelve con los datos de la notificación.
 */
export async function getUnregisteredNotification(userId, notificationId) {
  var notification = undefined
  const docSnap = await Firestore.getDoc(
    UnregisteredNotificationsDoc(userId, notificationId)
  )
  const document = docSnap.data()
  if (document) {
    if (document.fecha_puesta_disposicion)
      document.fecha_puesta_disposicion =
        document.fecha_puesta_disposicion.toDate()

    notification = { id: notificationId, ...document }
  }
  return notification
}

/**
 * Lista todos los unregisteredNotification para un usuario específico, aplicando filtros y transformaciones necesarias.
 *
 * @param {string} userId - El ID del usuario.
 * @param {Object} user - El objeto de usuario con detalles adicionales para el filtrado.
 * @param {number} [days] - Número de días hacia atrás desde hoy para incluir en la lista de resultados.
 * @param {number} [limit] - Número máximo de notificaciones a devolver tras ejecutar los filtros.
 * @returns {Promise<Array<Object>>} Una promesa que se resuelve con un array de notificaciones.
 */
export async function listAllUnregisteredNotifications(
  userId,
  user,
  days = 15,
  limit = 300 // establecer a 0 si se desea no limitar por defecto.
) {
  let querySnap
  // Establecer la hora, minutos, segundos y milisegundos a 0 porque
  // se quedan fuera notificaciones del dia calculado pero de antes de la hora actual y
  // asi obtenemos todas las del dia calculado a partir de la medianoche.
  const todaysDate = new Date()
  const from = new Date().setDate(todaysDate.getDate() - days)
  const fromDate = new Date(from)
  fromDate.setHours(0, 0, 0, 0)

  const to = new Date().setDate(todaysDate.getDate())
  const toDate = new Date(to)
  toDate.setHours(23, 59, 59, 999)

  querySnap = await Firestore.getDocs(
    days != 0
      ? Firestore.query(
          UnregisteredNotificationsCollection(userId),
          Firestore.orderBy('fecha_puesta_disposicion', 'desc'),
          Firestore.startAt(toDate),
          Firestore.endAt(fromDate)
        )
      : Firestore.query(
          UnregisteredNotificationsCollection(userId),
          Firestore.orderBy('fecha_puesta_disposicion', 'desc')
        )
  )

  let notifications = await Promise.all(
    querySnap.docs.map(async (doc) => {
      const document = doc.data()

      if (document.fecha_puesta_disposicion)
        document.fecha_puesta_disposicion =
          document.fecha_puesta_disposicion.toDate()

      return { ref: doc.ref, id: doc.id, ...document }
    })
  )

  // Nota: Como se trata de notificaciones pertenecientes a empresas no registradas, no tiene
  // sentido filtrar por empresa.
  /*
  // Filtramos por CIF, si es operador los Ids no coinciden
  if (userId !== user.id) {
    const cifs = user.cifs.map((c) => c.numeroDocIdentidad)
    notifications = notifications.filter((notification) => {
      return (
        cifs.includes(notification.nif_titular) &&
        user.cifsPermissions[notification.nif_titular]?.notificacionesActivas
      )
    })
  }*/

  // Cualquier usuario con permisos, aparte del superadmin, puede llegar a ver las notificaciones no registradas
  // se filtran como se hace con las notificaciones normales, a excepción de campos no existentes, como por
  // ejemplo 'manager', que se suprimen del codigo.
  //
  // Filtramos por filters
  const finalNotifications = filterNotifications(notifications, user)

  // Si limit es mayor que cero, devolvemos los limit primeros
  const limitedNotifications =
    limit > 0 ? finalNotifications.slice(0, limit) : finalNotifications
  return limitedNotifications
}

/**
 * Filtra las notificaciones según los roles y permisos del usuario.
 *
 * @param {Array<Object>} notificationsParam - Las notificaciones a filtrar.
 * @param {Object} user - El usuario para el cual se realiza el filtrado.
 * @returns {Array<Object>} Un array de notificaciones filtradas.
 */
export function filterNotifications(notificationsParam, user) {
  let notifications = notificationsParam
  let operatorRoles = user.operatorRoles ?? []
  const isSuperAdmin = !user.parentRef

  // Si es administrador o si es "Ver Todo", no filtramos nada
  if (!isSuperAdmin && operatorRoles[0]?.name !== 'noFilter') {
    notifications = notificationsParam.filter((n) => {
      return operatorRoles.some((role) => {
        const filter = [
          ...role.administrationLevelAllowed,
          ...role.autonomicAllowed,
          ...role.sourceEntityAllowed,
        ]
        if (!role.isCustom) {
          // Filtros genéricos
          if (
            filter.includes(n.administrationLevel) ||
            filter.includes(n.autonomicCode) ||
            filter.includes(n.codigo_organismo_emisor) ||
            filter.includes(n.codigo_organismo_emisor_raiz)
          )
            return true
        } else {
          // Filtros específicos del usuario
          if (filter.length > 0) {
            const isInFilter =
              filter.includes(n.administrationLevel) ||
              filter.includes(n.autonomicCode) ||
              filter.includes(n.codigo_organismo_emisor) ||
              filter.includes(n.codigo_organismo_emisor_raiz)
            if (isInFilter && role.negativeEntities) return false
            else if (!isInFilter && !role.negativeEntities) return false
          }

          // If there is a missing field we do not check the types and we accept the notification
          if (!role.types || !role.comparisonType) return true

          return role.comparisonType == '=='
            ? role.types.some(
                // Comparing the type with the ones in the metadatas.
                // We just need one type in the metadata to be in the filter type to accept the notification.
                // If the notification does not have metadatas, we return false
                (type) =>
                  n.metadatas?.some((m) => m.tipo === type) ||
                  n.conceptoTipo === type
              )
            : role.types.every(
                // Comparing the type with the ones in the metadatas.
                // All the types in the notification have to be different than the ones in the filter.
                // If the notification does not have metadatas, we return true
                // (all the types are different because there are no types)
                (type) =>
                  (n.metadatas?.every((m) => m.tipo !== type) ?? true) &&
                  n.conceptoTipo !== type
              )
        }
      })
    })
  }

  return notifications
}

/**
 * Calcula si han pasado mas de N horas recibidas a partir de la fecha
 *
 * @param {date} date - Fecha y hora
 * @param {number} [hours] - Número de horas a calcular
 */
export function moreThanNHours(date, hours = 12) {
  const HOUR = 1000 * 60 * 60
  const nHoursAgo = Date.now() - hours * HOUR

  if (!date) {
    return true
  }
  const result = date < nHoursAgo
  return result
}
